<script setup lang="ts">
interface SkinCard {
  id: string
  slug: string
  name: string
  creator_username: string | null
  background_url: string | null
  primary_color: string
  use_count: number
  favorite_count: number
  required_plan: 'free' | 'pro' | 'elite'
}

const route = useRoute()
const page = computed(() => Math.max(1, Number(route.query.page) || 1))
const LIMIT = 24

// Public, SSR: crawlers get the full grid in raw HTML. Pagination is via ?page=
// so page 2+ are their own crawlable URLs.
const { data, pending } = await useAsyncData(
  () => `skins-gallery-${page.value}`,
  () => $fetch<{ skins: SkinCard[], total: number }>('/api/skins', {
    query: { page: page.value, limit: LIMIT, sort: 'most_used' }
  }),
  { watch: [page] }
)

const skins = computed(() => data.value?.skins ?? [])
const total = computed(() => data.value?.total ?? 0)

const title = 'Skins: free animated link-in-bio themes | mychar.gg'
const description = 'Browse community-made skins for your mychar.gg page. Animated backgrounds, custom fonts, layouts, and colors. Preview any skin and apply it to your link in bio in one click.'

useSeoMeta({
  title,
  titleTemplate: null,
  ogTitle: 'mychar.gg skins',
  description,
  ogDescription: description
})

defineOgImageComponent('Saas')

useSchemaOrg([
  defineWebPage({ '@type': 'CollectionPage' })
])

const PLAN_BADGE: Record<string, { label: string, class: string }> = {
  pro: { label: 'Pro', class: 'bg-fuchsia-500/20 tint-fuchsia' },
  elite: { label: 'Elite', class: 'bg-amber-500/20 tint-amber' }
}
</script>

<template>
  <div>
    <UPageHero
      title="Skins"
      description="Community-made themes for your page. Preview any skin, then apply it in one click."
      :ui="{ container: 'py-16 sm:py-20' }"
    />

    <UContainer class="pb-24">
      <div
        v-if="skins.length > 0"
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        <!-- Stretched-link card: the title link covers the card; the creator
             link sits above it (nested anchors would be invalid HTML). -->
        <div
          v-for="skin in skins"
          :key="skin.id"
          class="group relative flex flex-col overflow-hidden rounded-2xl border border-default bg-elevated/40 shadow-(--surface-shadow) transition-colors hover:border-accented"
        >
          <div
            class="relative aspect-video overflow-hidden"
            :style="{ backgroundColor: skin.primary_color }"
          >
            <NuxtImg
              v-if="skin.background_url"
              :src="skin.background_url"
              :alt="`${skin.name} skin preview`"
              class="size-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
            <span
              v-if="PLAN_BADGE[skin.required_plan]"
              class="absolute right-2 top-2 rounded-full px-2 py-0.5 text-[11px] font-semibold"
              :class="PLAN_BADGE[skin.required_plan]!.class"
            >
              {{ PLAN_BADGE[skin.required_plan]!.label }}
            </span>
          </div>
          <div class="flex items-center justify-between gap-2 p-3.5">
            <div class="min-w-0">
              <NuxtLink
                :to="`/skins/${skin.slug}`"
                class="block truncate text-sm font-semibold text-highlighted group-hover:text-primary-400 transition-colors after:absolute after:inset-0"
              >
                {{ skin.name }}
              </NuxtLink>
              <NuxtLink
                v-if="skin.creator_username"
                :to="`/${skin.creator_username}`"
                class="relative z-10 block truncate text-xs text-muted hover:text-toned hover:underline"
              >
                by @{{ skin.creator_username }}
              </NuxtLink>
            </div>
            <span class="flex shrink-0 items-center gap-1 text-xs text-dimmed">
              <UIcon
                name="i-lucide-download"
                class="size-3.5"
              />
              {{ skin.use_count }}
            </span>
          </div>
        </div>
      </div>

      <div
        v-else-if="!pending"
        class="rounded-2xl border border-default p-12 text-center text-muted"
      >
        No skins to show yet.
      </div>

      <div
        v-if="total > LIMIT"
        class="mt-10 flex justify-center"
      >
        <UPagination
          :default-page="page"
          :items-per-page="LIMIT"
          :total="total"
          :to="(p: number) => ({ query: { page: p } })"
        />
      </div>
    </UContainer>
  </div>
</template>
